import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  api,
  FalhaApi,
  type AgendamentoApi,
  type ClienteApi,
  type TemplateResumoApi,
  type UsuarioResumoApi,
} from "../api";
import { useSessao } from "../estado/sessao";
import { Layout } from "../componentes/Layout";
import { PillStatusAgendamento } from "../componentes/Pill";
import {
  NOMES_DIA,
  NOMES_MES,
  fimDoDia,
  gradeDaSemana,
  gradeDoMes,
  inicioDaSemana,
  inicioDoMes,
  mesmoDia,
  somarDias,
} from "../utilCalendario";

type Visualizacao = "mes" | "semana";

const RETULO_STATUS: Record<string, string> = {
  AGENDADO: "Agendado",
  CONFIRMADO: "Confirmado",
  REALIZADO: "Realizado",
  CANCELADO: "Cancelado",
};

export function Calendario() {
  const { usuario } = useSessao();
  const admin = usuario?.papel === "ADMIN";

  const [referencia, setReferencia] = useState(() => new Date());
  const [visualizacao, setVisualizacao] = useState<Visualizacao>("mes");
  const [agendamentos, setAgendamentos] = useState<AgendamentoApi[] | null>(null);
  const [clientes, setClientes] = useState<ClienteApi[] | null>(null);
  const [usuarios, setUsuarios] = useState<UsuarioResumoApi[] | null>(null);
  const [templates, setTemplates] = useState<TemplateResumoApi[]>([]);
  const [dataFormulario, setDataFormulario] = useState<Date | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const dias = useMemo(
    () => (visualizacao === "mes" ? gradeDoMes(referencia) : gradeDaSemana(referencia)),
    [referencia, visualizacao]
  );
  const inicioIntervalo = visualizacao === "mes" ? inicioDoMes(referencia) : inicioDaSemana(referencia);

  function navegarPeriodo(direcao: -1 | 1) {
    setReferencia((atual) => {
      if (visualizacao === "semana") return somarDias(atual, direcao * 7);
      // Navegação por mês civil, sem o desvio causado por somar 30 dias (janeiro + 30,
      // por exemplo, ainda pode cair em janeiro).
      return new Date(atual.getFullYear(), atual.getMonth() + direcao, 1, 12);
    });
  }

  function carregarAgendamentos() {
    api
      .listarAgendamentos(dias[0]!.toISOString(), fimDoDia(dias[dias.length - 1]!).toISOString())
      .then(({ agendamentos }) => setAgendamentos(agendamentos))
      .catch(() => setErro("Falha ao carregar agenda"));
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(carregarAgendamentos, [referencia, visualizacao]);

  useEffect(() => {
    api.listarClientes().then(({ clientes }) => setClientes(clientes)).catch(() => setClientes([]));
    if (admin) {
      api.listarUsuarios().then(({ usuarios }) => setUsuarios(usuarios)).catch(() => setUsuarios([]));
      api.listarTemplates().then(({ templates }) => setTemplates(templates.filter((t) => t.status === "PUBLICADO"))).catch(() => setTemplates([]));
    }
  }, [admin]);

  const opcoesCliente = useMemo(
    () =>
      (clientes ?? []).map((c) => ({
        id: c.id,
        rotulo: c.administradora ? `${c.nome} (${c.administradora})` : c.nome,
      })),
    [clientes]
  );

  // Sem window.confirm: navegador de dialog nativo trava a página em iOS Safari (o alvo real do
  // PWA) até ser dispensado manualmente — cancelar direto no clique, mesmo padrão sem confirmação
  // já usado nos "Excluir" de Clientes.tsx.
  async function cancelar(id: string) {
    try {
      await api.cancelarAgendamento(id);
      carregarAgendamentos();
    } catch (e) {
      setErro(e instanceof FalhaApi ? e.message : "Falha ao cancelar");
    }
  }

  const [agendamentoParaIniciar, setAgendamentoParaIniciar] = useState<AgendamentoApi | null>(null);
  const [agendamentoParaVisualizar, setAgendamentoParaVisualizar] = useState<AgendamentoApi | null>(null);

  return (
    <Layout>
      <main className="agenda-pagina">
        <header className="agenda-cabecalho">
          <div>
            <span className="agenda-sobretitulo">PLANEJAMENTO</span>
            <h1>Agenda de vistorias</h1>
            <p>Organize as visitas e acompanhe a programação da equipe.</p>
          </div>
          <div className="agenda-visualizacao" role="group" aria-label="Modo de visualização">
            <button className={visualizacao === "mes" ? "ativo" : ""} onClick={() => setVisualizacao("mes")}>Mês</button>
            <button className={visualizacao === "semana" ? "ativo" : ""} onClick={() => setVisualizacao("semana")}>Semana</button>
          </div>
        </header>

        <section className="agenda-calendario">
          <div className="agenda-periodo">
            <button className="agenda-seta" onClick={() => navegarPeriodo(-1)} aria-label={`${visualizacao === "mes" ? "Mês" : "Semana"} anterior`}>‹</button>
            <div className="agenda-periodo-titulo">
              <small>{visualizacao === "mes" ? referencia.getFullYear() : "Período selecionado"}</small>
              <h2>
                {visualizacao === "mes"
                  ? NOMES_MES[referencia.getMonth()]
                  : `${inicioIntervalo.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })} – ${somarDias(inicioIntervalo, 6).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" })}`}
              </h2>
            </div>
            <button className="agenda-seta" onClick={() => navegarPeriodo(1)} aria-label={`Próxim${visualizacao === "mes" ? "o mês" : "a semana"}`}>›</button>
            <button className="agenda-hoje" onClick={() => setReferencia(new Date())}>Hoje</button>
          </div>

        {erro && (
          <div className="alerta alerta-erro" role="alert" style={{ marginTop: 12 }}>
            {erro}
          </div>
        )}

          <div className="agenda-grade-rolagem">
          <div className={`agenda-mes-grade ${visualizacao === "semana" ? "agenda-semana-grade" : ""}`}>
          {NOMES_DIA.map((nome) => (
            <div className="agenda-dia-semana" key={nome}>
              {nome}
            </div>
          ))}

          {dias.map((dia) => {
            const doMes = visualizacao === "semana" || dia.getMonth() === referencia.getMonth();
            const hoje = mesmoDia(dia, new Date());
            const doDia = (agendamentos ?? []).filter((a) => mesmoDia(new Date(a.dataHora), dia));

            return (
              <div
                key={dia.toISOString()}
                className={`agenda-celula${hoje ? " hoje" : ""}${doMes ? "" : " outro-mes"}`}
                onClick={() => admin && setDataFormulario(dia)}
                style={{ cursor: admin ? "pointer" : "default" }}
              >
                <span className="agenda-numero-dia">{dia.getDate()}</span>
                {doDia.map((a) => {
                  const podeIniciar = hoje && (a.status === "AGENDADO" || a.status === "CONFIRMADO");
                  return (
                    <div
                      key={a.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        // Clicar sempre abre pra visualizar, nunca age direto — era isso que
                        // faltava: um agendamento passado que nunca virou vistoria (esquecido, não
                        // só cancelado) precisa dar pra ver o que era antes de qualquer ação.
                        // Cancelar virou botão explícito dentro do painel de detalhe, em vez de
                        // acontecer só de clicar no card (bug real corrigido antes pro REALIZADO;
                        // esse mesmo risco ainda existia pra AGENDADO/CONFIRMADO do passado).
                        setAgendamentoParaVisualizar(a);
                      }}
                      title={`${RETULO_STATUS[a.status]} · clique para ver detalhes`}
                      className={`agenda-evento status-${a.status.toLowerCase()}`}
                    >
                      <span><strong>{a.cliente.nome}</strong>{admin ? <small>{a.vistoriador.nome}</small> : null}</span>
                      {podeIniciar && (
                        <button
                          className="agenda-evento-iniciar"
                          onClick={(e) => {
                            e.stopPropagation();
                            setAgendamentoParaIniciar(a);
                          }}
                        >
                          Iniciar
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
          </div>
        </div>
        </section>

        {!admin && (
          <p style={{ marginTop: 12, color: "var(--texto-suave)", fontSize: 13 }}>
            Só administradores podem criar ou cancelar agendamentos.
          </p>
        )}

        {dataFormulario && (
          <FormularioAgendamento
            data={dataFormulario}
            clientes={opcoesCliente}
            usuarios={usuarios ?? []}
            templates={templates}
            aoFechar={() => setDataFormulario(null)}
            aoCriar={() => {
              setDataFormulario(null);
              carregarAgendamentos();
            }}
          />
        )}

        {agendamentoParaIniciar && (
          <FormularioIniciarVistoria
            agendamento={agendamentoParaIniciar}
            aoFechar={() => setAgendamentoParaIniciar(null)}
          />
        )}

        {agendamentoParaVisualizar && (
          <DetalheAgendamento
            agendamento={agendamentoParaVisualizar}
            admin={admin}
            aoFechar={() => setAgendamentoParaVisualizar(null)}
            aoCancelar={async () => {
              await cancelar(agendamentoParaVisualizar.id);
              setAgendamentoParaVisualizar(null);
            }}
            aoIniciar={() => {
              setAgendamentoParaVisualizar(null);
              setAgendamentoParaIniciar(agendamentoParaVisualizar);
            }}
          />
        )}
      </main>
    </Layout>
  );
}

function DetalheAgendamento({
  agendamento,
  admin,
  aoFechar,
  aoCancelar,
  aoIniciar,
}: {
  agendamento: AgendamentoApi;
  admin: boolean;
  aoFechar: () => void;
  aoCancelar: () => void;
  aoIniciar: () => void;
}) {
  const [cancelando, setCancelando] = useState(false);
  const dataHora = new Date(agendamento.dataHora);
  const hoje = mesmoDia(dataHora, new Date());
  const podeIniciar = hoje && (agendamento.status === "AGENDADO" || agendamento.status === "CONFIRMADO");
  const podeCancelar = admin && (agendamento.status === "AGENDADO" || agendamento.status === "CONFIRMADO");

  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  return (
    <div ref={ref} className="cartao" style={{ padding: 16, marginTop: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
        <div>
          <h3 style={{ marginTop: 0, marginBottom: 4 }}>{agendamento.cliente.nome}</h3>
          <p style={{ margin: 0, color: "var(--texto-suave)", fontSize: 14 }}>
            {dataHora.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" })}
          </p>
        </div>
        <PillStatusAgendamento status={agendamento.status} />
      </div>

      <p style={{ margin: "12px 0 0", fontSize: 14 }}>
        <strong>Vistoriador:</strong> {agendamento.vistoriador.nome}
      </p>
      {agendamento.observacoes && (
        <p style={{ margin: "6px 0 0", fontSize: 14 }}>
          <strong>Observações:</strong> {agendamento.observacoes}
        </p>
      )}

      <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
        {agendamento.status === "REALIZADO" && agendamento.vistoriaId && (
          <Link className="botao botao-primario" style={{ marginTop: 0 }} to={`/vistorias/${agendamento.vistoriaId}`}>
            Ver vistoria
          </Link>
        )}
        {podeIniciar && (
          <button className="botao botao-primario" style={{ marginTop: 0 }} onClick={aoIniciar}>
            Iniciar vistoria
          </button>
        )}
        {podeCancelar && (
          <button
            className="botao botao-texto"
            disabled={cancelando}
            onClick={async () => {
              setCancelando(true);
              await aoCancelar();
            }}
          >
            {cancelando ? "Cancelando…" : "Cancelar agendamento"}
          </button>
        )}
        <button className="botao botao-texto" type="button" onClick={aoFechar}>
          Fechar
        </button>
      </div>
    </div>
  );
}

function FormularioIniciarVistoria({
  agendamento,
  aoFechar,
}: {
  agendamento: AgendamentoApi;
  aoFechar: () => void;
}) {
  const navegar = useNavigate();
  const [responsavelNome, setResponsavelNome] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const ref = useRef<HTMLFormElement>(null);
  // Sem overlay: o formulário nasce abaixo do grid, então precisa de scroll até ele pra não
  // parecer que o clique em "Iniciar" não fez nada — importante no mobile, onde o grid inteiro
  // pode ocupar mais que uma tela.
  useEffect(() => {
    ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  async function aoEnviar(evento: FormEvent) {
    evento.preventDefault();
    setErro(null);
    setSalvando(true);
    try {
      const { vistoria } = await api.criarVistoria({
        clienteId: agendamento.clienteId,
        agendamentoId: agendamento.id,
        dataVistoria: new Date().toISOString().slice(0, 10),
        responsavelNome,
      });
      navegar(`/vistorias/${vistoria.id}`);
    } catch (e) {
      setErro(e instanceof FalhaApi ? e.message : "Falha ao iniciar vistoria");
      setSalvando(false);
    }
  }

  return (
    <form ref={ref} className="cartao" onSubmit={aoEnviar} style={{ padding: 16, marginTop: 16 }}>
      <h3 style={{ marginTop: 0 }}>Iniciar vistoria</h3>
      <p style={{ color: "var(--texto-suave)", marginTop: -8 }}>{agendamento.cliente.nome}</p>
      {erro && (
        <div className="alerta alerta-erro" role="alert">
          {erro}
        </div>
      )}

      <label htmlFor="iv-responsavel">Nome de quem está acompanhando a vistoria no local (opcional)</label>
      <input
        id="iv-responsavel"
        value={responsavelNome}
        onChange={(e) => setResponsavelNome(e.target.value)}
        autoFocus
      />

      <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
        <button className="botao botao-primario" style={{ marginTop: 0 }} type="submit" disabled={salvando}>
          {salvando ? "Iniciando…" : "Iniciar"}
        </button>
        <button className="botao botao-texto" type="button" onClick={aoFechar}>
          Cancelar
        </button>
      </div>
    </form>
  );
}

function FormularioAgendamento({
  data,
  clientes,
  usuarios,
  templates,
  aoFechar,
  aoCriar,
}: {
  data: Date;
  clientes: { id: string; rotulo: string }[];
  usuarios: UsuarioResumoApi[];
  templates: TemplateResumoApi[];
  aoFechar: () => void;
  aoCriar: () => void;
}) {
  const [clienteId, setClienteId] = useState("");
  const [vistoriadorId, setVistoriadorId] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [observacoes, setObservacoes] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const ref = useRef<HTMLFormElement>(null);
  useEffect(() => {
    ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  async function aoEnviar(evento: FormEvent) {
    evento.preventDefault();
    setErro(null);
    setSalvando(true);
    try {
      // Sem hora nem duração no formulário — na prática o vistoriador tem a agenda do dia
      // inteiro pra cumprir, não um horário fixo. Guarda um horário interno fixo só pra manter
      // a coluna preenchida no banco; nenhuma tela mostra esse valor.
      const dataHora = new Date(data);
      dataHora.setHours(8, 0, 0, 0);
      await api.criarAgendamento({
        clienteId,
        vistoriadorId,
        templateId: templateId || undefined,
        dataHora: dataHora.toISOString(),
        observacoes,
      });
      aoCriar();
    } catch (e) {
      setErro(e instanceof FalhaApi ? e.message : "Falha ao criar agendamento");
    } finally {
      setSalvando(false);
    }
  }

  return (
    <form ref={ref} className="cartao" onSubmit={aoEnviar} style={{ padding: 16, marginTop: 16 }}>
      <h3 style={{ marginTop: 0 }}>Novo agendamento</h3>
      <p style={{ color: "var(--texto-suave)", marginTop: -8 }}>
        {data.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" })}
      </p>
      {erro && (
        <div className="alerta alerta-erro" role="alert">
          {erro}
        </div>
      )}

      <label htmlFor="ag-cliente">Cliente</label>
      <select id="ag-cliente" value={clienteId} onChange={(e) => setClienteId(e.target.value)} required>
        <option value="" disabled>
          Selecione…
        </option>
        {clientes.map((c) => (
          <option key={c.id} value={c.id}>
            {c.rotulo}
          </option>
        ))}
      </select>

      <label htmlFor="ag-vistoriador">Vistoriador</label>
      <select id="ag-vistoriador" value={vistoriadorId} onChange={(e) => setVistoriadorId(e.target.value)} required>
        <option value="" disabled>
          Selecione…
        </option>
        {usuarios.map((u) => (
          <option key={u.id} value={u.id}>
            {u.nome}
          </option>
        ))}
      </select>

      <label htmlFor="ag-template">Modelo da vistoria</label>
      <select id="ag-template" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
        <option value="">Modelo geral padrão</option>
        {templates.map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
      </select>
      <small style={{ display: "block", marginTop: -7, marginBottom: 10, color: "var(--texto-suave)" }}>
        Selecione um modelo específico quando o prédio tiver um checklist próprio.
      </small>

      <label htmlFor="ag-observacoes">Observações</label>
      <input id="ag-observacoes" value={observacoes} onChange={(e) => setObservacoes(e.target.value)} />

      <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
        <button className="botao botao-primario" style={{ marginTop: 0 }} type="submit" disabled={salvando}>
          {salvando ? "Salvando…" : "Salvar"}
        </button>
        <button className="botao botao-texto" type="button" onClick={aoFechar}>
          Cancelar
        </button>
      </div>
    </form>
  );
}
